import { useEffect, useRef, useState } from "react"
import {
  Button,
  LoadingIcon16,
  Dialog,
  CopyIcon16,
} from "@unlimint/admin-ui-kit"
import { useContextSelector as useCS } from "use-context-selector"

import { apiService } from "infrastructure/services/ApiService"
import { errorToText } from "infrastructure/utils/errorToText"

import { CustomizationContext } from "domain/customization/CustomizationContext"
import { customizationSelectors } from "domain/customization/selectors"
import { useOpenable } from "domain/hooks/useOpenable"
import { formatDateTimeList } from "domain/core/utils"

import * as S from "./styled"
import { ConfirmDeleteThemes } from "../ConfirmDeleteThemes"

const enum GridStateState {
  Loading = "loading",
  Error = "error",
  Empty = "empty",
  List = "list",
}

export const ThemesGrid = () => {
  const data = useCS(CustomizationContext, customizationSelectors.data)
  const list = useCS(CustomizationContext, customizationSelectors.list)
  const error = useCS(CustomizationContext, customizationSelectors.error)
  const stylesUpdate = useCS(
    CustomizationContext,
    customizationSelectors.update
  )

  const [gridStateState, setGridStateState] = useState(GridStateState.Loading)
  const [selectedRowName, setSelectedRowName] = useState(null)
  const [scrollWidth, setScrollWidth] = useState(0)

  const [deleteError, setDeleteError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const listRef = useRef<HTMLDivElement>(null)

  const deleteOpenable = useOpenable()

  useEffect(() => {
    if (!list && !error) {
      setGridStateState(GridStateState.Loading)
      return
    }

    if (error) {
      setGridStateState(GridStateState.Error)
      return
    }

    if (list && !list.length) {
      setGridStateState(GridStateState.Empty)
      return
    }

    setGridStateState(GridStateState.List)
  }, [list, error])

  useEffect(() => {
    if (gridStateState !== GridStateState.List) return

    const el = listRef.current
    if (!el) return

    const scrollbarWidth = el.offsetWidth - el.clientWidth
    setScrollWidth(scrollbarWidth)
  }, [gridStateState])

  const onSelectRowName = (e: Event, name: string) => {
    setSelectedRowName(name)
    deleteOpenable.onOpen(e)
  }

  useEffect(() => {
    if (!deleteOpenable.open) {
      setDeleteError(null)
      setSelectedRowName(null)
    }
  }, [deleteOpenable.open])

  const deleteTheme = async () => {
    setDeleteError(null)
    setIsDeleting(true)

    try {
      await apiService({
        url: `/v1/merchants/styles`,
        method: "DELETE",
        data: {
          names: [selectedRowName],
        },
      })

      const newList = list.filter((item) => selectedRowName !== item.name)

      stylesUpdate({
        data: {
          ...data,
          list: newList || [],
        },
      })
      setSelectedRowName(null)
      deleteOpenable.onClose()
    } catch (err) {
      if (err.response.data?.details) {
        const values = Object.values(err.response.data?.details)
        const errorText = errorToText(values[0])
        setDeleteError(errorText)
        return
      }

      setDeleteError("Something went wrong. Try again later.")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <S.GridHeader $scrollWidth={scrollWidth}>
        <S.GridHeaderItem $maxWidth={476} $minWidth={376}>
          Customization name
        </S.GridHeaderItem>
        <S.GridHeaderItem $maxWidth={316} $minWidth={216}>
          Author
        </S.GridHeaderItem>
        <S.GridHeaderItem $maxWidth={276} $minWidth={120}>
          Created
        </S.GridHeaderItem>
        <S.GridHeaderItem $maxWidth={276} $minWidth={120}>
          Edited
        </S.GridHeaderItem>
        <S.GridHeaderItem $maxWidth={64} $minWidth={64} />
      </S.GridHeader>

      {gridStateState === GridStateState.Loading && (
        <S.GridListEmpty>
          <LoadingIcon16 />
        </S.GridListEmpty>
      )}
      {gridStateState === GridStateState.Error && (
        <S.GridListEmpty>
          Error: Can not load config. Something went wrong.
        </S.GridListEmpty>
      )}

      {gridStateState === GridStateState.Empty && (
        <S.GridListEmpty>
          <div>No customizations have been created yet.</div>
        </S.GridListEmpty>
      )}

      {gridStateState === GridStateState.List && (
        <S.GridList ref={listRef} $scrollWidth={scrollWidth}>
          {list.map((item) => {
            return (
              <S.GridListRow key={item.name}>
                <S.GridListColumn $maxWidth={476} $minWidth={376}>
                  <S.Link to={item.name}>{item.name}</S.Link>
                  <S.CopyIcon>
                    <CopyIcon16 />
                  </S.CopyIcon>
                </S.GridListColumn>

                <S.GridListColumn $maxWidth={316} $minWidth={216}>
                  {item.author}
                </S.GridListColumn>

                <S.GridListColumn $maxWidth={276} $minWidth={120}>
                  {formatDateTimeList(item.createdAt)}
                </S.GridListColumn>

                <S.GridListColumn $maxWidth={276} $minWidth={120}>
                  {formatDateTimeList(item.updatedAt)}
                </S.GridListColumn>

                <S.GridListColumn $maxWidth={64} $minWidth={64}>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={(e) => onSelectRowName(e, item.name)}
                  >
                    Delete
                  </Button>
                </S.GridListColumn>
              </S.GridListRow>
            )
          })}
        </S.GridList>
      )}

      <Dialog open={deleteOpenable.open} onClose={deleteOpenable.onClose}>
        <ConfirmDeleteThemes
          onCancel={deleteOpenable.onClose}
          onSubmit={deleteTheme}
          error={deleteError}
          isSubmitting={isDeleting}
        />
      </Dialog>
    </>
  )
}
